import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Save, Trash2, Upload, X, Link as LinkIcon } from "lucide-react";
import { api } from "../lib/api";

const MAX_FILE_BYTES = 1_500_000; // 1.5 MB raw — once base64'd this is ~2 MB, comfortably under document limits and route payloads.

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const csvToList = (s) =>
  (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const listToCsv = (arr) => (arr || []).join(", ");

const EMPTY = {
  slug: "",
  name: "",
  description: "",
  image_url: "",
  served_cold_visible: true,
  served_cold_label: "Served Cold",
  notesCsv: "",
  sort_order: 0,
};

export const ProductsManager = ({ products, onChange }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errs, setErrs] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const startCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY, sort_order: products.length });
    setErrs({});
    setShowForm(true);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      slug: p.slug,
      name: p.name,
      description: p.description,
      image_url: p.image_url || "",
      served_cold_visible: !!p.served_cold_visible,
      served_cold_label: p.served_cold_label || "Served Cold",
      notesCsv: listToCsv(p.notes),
      sort_order: p.sort_order || 0,
    });
    setErrs({});
    setShowForm(true);
  };

  const cancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY);
    setErrs({});
  };

  const onPickFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`Image must be under ${Math.round(MAX_FILE_BYTES / 1_000_000)} MB`);
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setForm((s) => ({ ...s, image_url: dataUrl }));
    } catch {
      toast.error("Could not read the file");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.description.trim()) e.description = "Required";
    if (!form.slug.trim()) e.slug = "Required";
    else if (!/^[a-z0-9-]+$/.test(form.slug)) e.slug = "Lowercase letters, numbers, and dashes only";
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const save = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const payload = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      image_url: form.image_url.trim(),
      served_cold_visible: !!form.served_cold_visible,
      served_cold_label: form.served_cold_label.trim() || "Served Cold",
      notes: csvToList(form.notesCsv),
      sort_order: Number(form.sort_order) || 0,
    };
    setSaving(true);
    try {
      if (editingId) {
        const r = await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
        onChange((list) => list.map((p) => (p.id === editingId ? r.data : p)));
      } else {
        const r = await api.post("/products", payload);
        toast.success("Product added");
        onChange((list) => [...list, r.data]);
      }
      cancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"?`)) return;
    setDeletingId(p.id);
    try {
      await api.delete(`/products/${p.id}`);
      onChange((list) => list.filter((x) => x.id !== p.id));
      toast.success("Product deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setDeletingId(null);
    }
  };

  const sorted = [...products].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div data-testid="products-manager">
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-[#8D99AE]" data-testid="products-manager-count">
          {products.length} {products.length === 1 ? "product" : "products"}
        </div>
        {!showForm && (
          <button
            data-testid="products-add-new"
            onClick={startCreate}
            className="inline-flex items-center gap-2 bg-[#2C1E16] text-[#FAF9F6] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#3E2D24] transition-colors"
          >
            <Plus size={15} strokeWidth={1.6} />
            Add product
          </button>
        )}
      </div>

      {showForm && (
        <form
          data-testid="product-form"
          onSubmit={save}
          className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8 mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-xl text-[#2C1E16]">
              {editingId ? "Edit product" : "New product"}
            </h3>
            <button type="button" onClick={cancel} className="text-[#8D99AE] hover:text-[#2C1E16]" data-testid="product-form-cancel">
              <X size={18} strokeWidth={1.6} />
            </button>
          </div>

          <div className="grid md:grid-cols-12 gap-x-8 gap-y-6">
            <div className="md:col-span-4">
              <span className="block text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-2">Photo</span>
              <div className="aspect-[4/5] rounded-2xl border border-[#E8E3DD] bg-[#FAF9F6] overflow-hidden flex items-center justify-center">
                {form.image_url ? (
                  <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-[#8D99AE]">No image</span>
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="product-image-upload-btn"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs bg-[#2C1E16] text-[#FAF9F6] hover:bg-[#3E2D24] disabled:opacity-60 rounded-full px-3 py-2 transition-colors"
                >
                  {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} strokeWidth={1.7} />}
                  Upload
                </button>
                {form.image_url && (
                  <button
                    type="button"
                    onClick={() => setForm((s) => ({ ...s, image_url: "" }))}
                    data-testid="product-image-clear"
                    className="inline-flex items-center justify-center text-xs text-[#B33A3A] hover:bg-[#FAF9F6] rounded-full px-3 py-2"
                  >
                    Remove
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="product-image-file"
                  onChange={(e) => onPickFile(e.target.files?.[0])}
                />
              </div>
              <div className="mt-3">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.16em] text-[#8D99AE] mb-1 inline-flex items-center gap-1">
                    <LinkIcon size={11} strokeWidth={1.7} /> Or paste an image URL
                  </span>
                  <input
                    data-testid="product-input-image-url"
                    value={form.image_url.startsWith("data:") ? "" : form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="https://…"
                    className="admin-input text-sm"
                  />
                </label>
                <p className="mt-1 text-[10px] text-[#8D99AE]">
                  Tip: max 1.5&nbsp;MB if uploading. PNG/JPG/WebP.
                </p>
              </div>
            </div>

            <div className="md:col-span-8 grid sm:grid-cols-2 gap-x-6 gap-y-5">
              <Field label="Name" testId="product-input-name-field" error={errs.name}>
                <input
                  data-testid="product-input-name"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((s) => ({
                      ...s,
                      name,
                      slug: editingId ? s.slug : (s.slug ? s.slug : slugify(name)),
                    }));
                  }}
                  placeholder="Vanilla Latte"
                  className="admin-input"
                />
              </Field>
              <Field label="Slug (URL handle)" testId="product-input-slug-field" error={errs.slug}>
                <input
                  data-testid="product-input-slug"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  placeholder="vanilla-latte"
                  className="admin-input"
                />
              </Field>
              <Field label="Description" testId="product-input-description-field" className="sm:col-span-2" error={errs.description}>
                <textarea
                  data-testid="product-input-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Double-shot espresso, whole milk, and real vanilla…"
                  className="admin-input resize-none pt-2"
                />
              </Field>
              <Field label="Flavor notes (comma-separated)" testId="product-input-notes-field" className="sm:col-span-2">
                <input
                  data-testid="product-input-notes"
                  value={form.notesCsv}
                  onChange={(e) => setForm({ ...form, notesCsv: e.target.value })}
                  placeholder="Smooth, Vanilla bean, Lightly sweet"
                  className="admin-input"
                />
              </Field>

              <Field label="Served-cold tag text" testId="product-input-cold-label-field">
                <input
                  data-testid="product-input-cold-label"
                  value={form.served_cold_label}
                  onChange={(e) => setForm({ ...form, served_cold_label: e.target.value })}
                  placeholder="Served Cold"
                  className="admin-input"
                />
              </Field>
              <Field label="Sort order" testId="product-input-sort-field">
                <input
                  data-testid="product-input-sort"
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  className="admin-input"
                />
              </Field>

              <label className="sm:col-span-2 inline-flex items-center gap-3 cursor-pointer select-none">
                <input
                  data-testid="product-input-cold-visible"
                  type="checkbox"
                  checked={form.served_cold_visible}
                  onChange={(e) => setForm({ ...form, served_cold_visible: e.target.checked })}
                  className="h-4 w-4 accent-[#2C1E16]"
                />
                <span className="text-sm text-[#2C1E16]">Show &ldquo;{form.served_cold_label || "Served Cold"}&rdquo; tag on the bottle card</span>
              </label>
            </div>
          </div>

          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
            <button
              type="button"
              onClick={cancel}
              className="text-sm px-5 py-2.5 text-[#5C4E46] hover:text-[#2C1E16]"
            >
              Cancel
            </button>
            <button
              data-testid="product-form-save"
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 bg-[#2C1E16] text-[#FAF9F6] hover:bg-[#3E2D24] disabled:opacity-60 transition-colors rounded-full px-6 py-2.5 text-sm font-medium"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} strokeWidth={1.6} />}
              {editingId ? "Save changes" : "Create product"}
            </button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {sorted.length === 0 && (
          <div data-testid="products-manager-empty" className="sm:col-span-2 rounded-3xl border border-[#E8E3DD] bg-white p-10 text-center text-[#5C4E46]">
            No products yet. Add your first flavor above.
          </div>
        )}
        {sorted.map((p) => (
          <article
            key={p.id}
            data-testid={`product-row-${p.id}`}
            className="rounded-2xl border border-[#E8E3DD] bg-white p-5 flex gap-4"
          >
            <div className="w-20 h-24 rounded-xl bg-[#FAF9F6] border border-[#E8E3DD] overflow-hidden flex-shrink-0">
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[10px] text-[#8D99AE]">No image</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display text-lg text-[#2C1E16] leading-tight truncate">{p.name}</div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-[#8D99AE] mt-0.5">{p.slug}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    data-testid={`product-edit-${p.id}`}
                    onClick={() => startEdit(p)}
                    className="p-2 rounded-full hover:bg-[#FAF9F6] text-[#2C1E16]"
                    title="Edit"
                  >
                    <Pencil size={15} strokeWidth={1.6} />
                  </button>
                  <button
                    data-testid={`product-delete-${p.id}`}
                    onClick={() => remove(p)}
                    disabled={deletingId === p.id}
                    className="p-2 rounded-full hover:bg-[#FAF9F6] text-[#B33A3A] disabled:opacity-50"
                    title="Delete"
                  >
                    {deletingId === p.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} strokeWidth={1.6} />}
                  </button>
                </div>
              </div>
              <p className="text-xs text-[#5C4E46] mt-2 line-clamp-2">{p.description}</p>
              {p.notes?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {p.notes.slice(0, 3).map((n) => (
                    <span key={n} className="text-[10px] text-[#8D99AE] bg-[#8D99AE]/10 px-2 py-0.5 rounded-full">{n}</span>
                  ))}
                  {p.notes.length > 3 && <span className="text-[10px] text-[#8D99AE]">+{p.notes.length - 3}</span>}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, children, testId, error, className = "" }) => (
  <label className={`block ${className}`} data-testid={testId}>
    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-1">{label}</span>
    {children}
    {error && <span className="mt-1 block text-xs text-[#B33A3A]">{error}</span>}
  </label>
);
